import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Evidence } from '../../types/story';
import { Search, Eye } from 'lucide-react';

interface InvestigationSceneProps {
  evidence: Evidence[];
  onEvidenceDiscovered: (evidenceId: string) => void;
  onComplete: () => void;
}

export const InvestigationScene: React.FC<InvestigationSceneProps> = ({
  evidence,
  onEvidenceDiscovered,
  onComplete
}) => {
  const [selectedEvidence, setSelectedEvidence] = useState<string | null>(null);
  const [foundEvidence, setFoundEvidence] = useState<Set<string>>(new Set());

  const handleEvidenceClick = (evidenceId: string) => {
    if (!foundEvidence.has(evidenceId)) {
      setFoundEvidence(prev => new Set([...prev, evidenceId]));
      onEvidenceDiscovered(evidenceId);
    }
    setSelectedEvidence(evidenceId);
  };

  const allFound = foundEvidence.size === evidence.length;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
      <h3 className="text-xl font-bold mb-4 flex items-center">
        <Search className="w-6 h-6 mr-2 text-blue-600" />
        调查现场
      </h3>

      <div className="mb-4">
        <p className="text-gray-600 mb-2">点击物品进行调查：</p>
        <div className="grid grid-cols-3 gap-4">
          {evidence.map((item, index) => (
            <motion.button
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleEvidenceClick(item.id)}
              className={`
                relative p-4 rounded-lg border-2 transition-all
                ${foundEvidence.has(item.id)
                  ? 'bg-green-50 border-green-300'
                  : 'bg-gray-50 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }
              `}
            >
              <div className="text-2xl mb-2">{item.icon || '🔍'}</div>
              <div className="text-sm font-medium text-gray-700">
                {foundEvidence.has(item.id) ? item.name : '???'}
              </div>
              {foundEvidence.has(item.id) && (
                <Eye className="absolute top-2 right-2 w-4 h-4 text-green-600" />
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {selectedEvidence && foundEvidence.has(selectedEvidence) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200"
        >
          <h4 className="font-semibold text-blue-900 mb-1">
            {evidence.find(e => e.id === selectedEvidence)?.name}
          </h4>
          <p className="text-sm text-blue-700">
            {evidence.find(e => e.id === selectedEvidence)?.description}
          </p>
        </motion.div>
      )}

      <div className="flex justify-end">
        <button
          onClick={onComplete}
          disabled={!allFound}
          className={`
            px-6 py-2 rounded-lg font-medium transition
            ${allFound
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          {allFound ? '继续调查' : `已发现 ${foundEvidence.size}/${evidence.length} 个线索`}
        </button>
      </div>
    </div>
  );
};